// next-app/src/hooks/use-training.ts
'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth-store';
import { queryKeys } from './query-keys';
import type { TrainingProgressResponse } from '@/types';

// ============================================================================
// Training Progress Query
// ============================================================================

interface UseTrainingProgressOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch training progress for an event.
 *
 * Usage:
 * ```tsx
 * const { data, isLoading } = useTrainingProgress(eventId);
 * const progress = data?.progress;
 * ```
 */
export function useTrainingProgress(eventId: number, options: UseTrainingProgressOptions = {}) {
  const token = useAuthStore((state) => state.dealerToken);

  return useQuery({
    queryKey: queryKeys.training.progress(eventId),
    queryFn: async () => {
      const { response, result } = await api.getTrainingProgress(eventId);

      if (!response.ok) {
        throw new Error('Failed to load training progress');
      }

      return result as TrainingProgressResponse;
    },
    enabled: options.enabled !== false && Boolean(token) && Boolean(eventId),
    staleTime: 60000, // 1 minute - training progress doesn't change often
  });
}

// ============================================================================
// Mark Training Complete Mutation
// ============================================================================

interface UseMarkTrainingCompleteOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

type TrainingSection = 'video' | 'overview' | 'details' | 'assets';

/**
 * Hook to mark a training section as complete.
 *
 * Usage:
 * ```tsx
 * const markComplete = useMarkTrainingComplete(eventId);
 * markComplete.mutate('video'); // Mark video section complete
 * ```
 */
export function useMarkTrainingComplete(
  eventId: number,
  options: UseMarkTrainingCompleteOptions = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (section: TrainingSection) => {
      const { response, result } = await api.markTrainingComplete({
        event_id: eventId,
        section,
      });

      if (!response.ok) {
        throw new Error('Failed to mark training complete');
      }

      return result;
    },
    onSuccess: () => {
      // Invalidate training progress to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.training.progress(eventId) });

      options.onSuccess?.();
    },
    onError: (error: Error) => {
      console.error('[useMarkTrainingComplete] Error:', error.message);
      options.onError?.(error);
    },
  });
}

// ============================================================================
// Training Completion Check
// ============================================================================

/**
 * Hook to check if training is complete.
 * Returns true if all sections are completed.
 */
export function useIsTrainingComplete(eventId: number) {
  const { data } = useTrainingProgress(eventId);
  const progress = data?.progress;

  if (!progress) return false;

  return (
    progress.video_watched &&
    progress.overview_completed &&
    progress.details_completed &&
    progress.assets_downloaded
  );
}

/**
 * Calculate training progress percentage.
 */
export function calculateTrainingProgress(
  progress: TrainingProgressResponse['progress'] | undefined
): number {
  if (!progress) return 0;

  let completed = 0;
  if (progress.video_watched) completed++;
  if (progress.overview_completed) completed++;
  if (progress.details_completed) completed++;
  if (progress.assets_downloaded) completed++;

  return (completed / 4) * 100;
}
